import React, {Component, FC, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import DraggableModal from "../UI/DraggableModal";
import {Button, CloseButton, Form, Modal} from 'react-bootstrap';
import Required from "../Required";
import {Tab, Tabs} from "../Tabs";
import Input from "@material-ui/core/Input";
import SearchIcon from "@mui/icons-material/Search";
import useDebounce from "../../hooks/useDebounce";
import {ProjectShort, RoleGroup, User} from "../../models/Mng";
import useAPI from "../../hooks/useAPI";
import {Tag} from "../../models/Tags";
import useToast from "../../hooks/useToast";
import Select from "react-select";
import {useMediaQuery} from "@mui/material";
import {PlusLg} from "react-bootstrap-icons";
import TooltippedElement from "../common/TooltippedElement";

interface AddAdminTagProps {
    show: boolean,
    onHide: () => void,
    refetchTags: () => Promise<void>,
    tagToEdit: number | null
}

interface AddAdminTagTabProps {
    tag: Tag,
    handleChangeTag: (fieldName: keyof Tag, value: Tag[keyof Tag]) => void,
    strategies: any[]
}

interface InputOverrideProps {
    value: string,
    onChange: (value: string) => void,
    placeholder?: string
}

const newTag: Tag = {
    id: 0,
    name: '',
    groups: [],
    users: [],
    projs: []
}

const tabs = [
    {
        label: 'roles',
        tabName: 'Выбрать ролевые группы',
        renderContent: (props: AddAdminTagTabProps) => <RolesTab {...props}/>
    },
    {
        label: 'fio',
        tabName: 'Выбрать ФИО',
        renderContent: (props: AddAdminTagTabProps) => <FioTab {...props}/>
    },
    {
        label: 'projects',
        tabName: 'Привязать/отвязать проект',
        renderContent: (props: AddAdminTagTabProps) => <ProjectsTab {...props}/>
    }
]

export default function AddAdminTag({show, onHide, refetchTags, tagToEdit}: AddAdminTagProps){
    const api = useAPI();
    const addToast = useToast();

    const [tag, setTag] = useState<Tag>(newTag);
    const [strategies, setStrategies] = useState<any[]>([]);

    const handleChangeTag = (fieldName: keyof Tag, value: Tag[keyof Tag]) => {
        setTag(prevState => ({...prevState, [fieldName]: value}));
    }

    const handleSave = async () => {
        if (!tag.name) {
            addToast.error('Необходимо ввести название тега');
        } else {
            const response = await api.post(`/mng/labels/${tag.id}`, tag);
            if (response.id) {
                onHide();
                await refetchTags()
                addToast.success('Успешно сохранено!');
                setTag(newTag)
            }
        }
    }

    useEffect(() => {
        const getTag = async () => {
            const tag = await api.get(`/mng/labels/${tagToEdit}`)
            setTag(tag)
        }
        if(tagToEdit){
            getTag();
        } else {
            setTag(newTag);
        }
    }, [tagToEdit]);

    useEffect(() => {
        const getStrategies = async () => {
            const strategies = await api.get(`/stats/projs/structure?year=${new Date().getFullYear()}`);
            setStrategies(strategies.filter(strategy => strategy.num !== 'СП0'))
        }
        getStrategies();
    }, []);

    return <DraggableModal
        show={show}
        onHide={onHide}
        headerNode='Добавить тег'
        size='lg'
        style={{ maxHeight: '98vh' }}
    >
        <Modal.Body>
            <Form>
                <Form.Group className='mb-4'>
                    <Form.Label>Название тега <Required/></Form.Label>
                    <Form.Control
                        value={tag.name}
                        onChange={(event) => handleChangeTag('name', event.target.value)}
                    />
                </Form.Group>
                <Tabs>
                    {tabs.map(tab => <Tab key={tab.label} label={tab.label} tabName={tab.tabName}>
                        <div>
                            {tab.renderContent({tag, handleChangeTag, strategies})}
                        </div>
                    </Tab>)}
                </Tabs>
            </Form>
        </Modal.Body>
        <Modal.Footer>
            <div className='d-flex gap-2 justify-content-end'>
                <Button variant='outline-secondary' onClick={onHide}>Отменить</Button>
                <Button onClick={handleSave}>Сохранить</Button>
            </div>
        </Modal.Footer>
    </DraggableModal>
}

function useTagTab<T>(fetchLink: string, searchValue: string, callback?: (options: T[]) => any) {
    const api = useAPI();

    const [options, setOptions] = useState([]);
    const debouncedSearchValue: string = useDebounce(searchValue, 400);

    useEffect(() => {
        const getOptions = async () => {
            const options: T[] = await api.get(fetchLink + debouncedSearchValue);
            setOptions(callback ? callback(options) : options)
        }
        getOptions()
    }, [debouncedSearchValue, fetchLink]);

    return options
}

function RolesTab({tag, handleChangeTag}: AddAdminTagTabProps) {
    const [searchValue, setSearchValue] = useState<string>('');
    const roleGroups = useTagTab<{title: string, id: string}>(
        '/mng/roles/groups?title=',
        searchValue,
        (groups) => groups.map(group => ({title: group.title, groupCode: group.id}))
    )
    const smallScreen = useMediaQuery('(max-height: 730px)')

    const renderOptionView = useCallback((roleGroup: RoleGroup) => {
        return <div className='d-flex gap-2' key={roleGroup.groupCode}>
            <input
                type='checkbox'
                defaultChecked={tag.groups.some(group => group.groupCode === roleGroup.groupCode)}
                onChange={(event) => handleChangeTag(
                    'groups',
                    event.target.checked ? [...tag.groups, roleGroup] : tag.groups.filter(group => group.groupCode !== roleGroup.groupCode)
                )}
            />
            {roleGroup.title}
        </div>
    }, [tag, handleChangeTag])

    return (
        <div>
            <InputOverride value={searchValue} onChange={setSearchValue} placeholder='Поиск по названию группы'/>
            <div className='d-flex flex-column gap-2 mt-3' style={{overflow: 'auto', maxHeight: smallScreen ? '20vh' : '40vh'}}>
                {tag.groups.map(roleGroup => renderOptionView(roleGroup))}
                {roleGroups
                    .filter(roleGroup => tag.groups.every(group => group.groupCode !== roleGroup.groupCode))
                    .map(roleGroup => renderOptionView(roleGroup))}
            </div>
        </div>
    )

}

function FioTab({tag, handleChangeTag}: AddAdminTagTabProps){
    const [searchValue, setSearchValue] = useState<string>('');
    const users = useTagTab<User>("/users?name=", searchValue)
    const smallScreen = useMediaQuery('(max-height: 730px)')

    const renderOptionView = useCallback((user: User) => {
        return <div className='d-flex align-items-start gap-2' key={user.sAMAccountName}>
            <div>
                <input
                    type='checkbox'
                    defaultChecked={tag.users.some(value => value.sAMAccountName === user.sAMAccountName)}
                    onChange={(event) => handleChangeTag(
                        'users',
                        event.target.checked ? [...tag.users, user] : tag.users.filter(value => value.sAMAccountName !== user.sAMAccountName)
                    )}
                />
            </div>
            <div className='d-flex flex-column'>
                <span style={{fontSize: 14, fontWeight: 600}}>
                    {user.displayName || `${user.lastName} ${user.firstName} ${user.middleName}`}
                </span>
                <span style={{fontSize: 12, color: '#6c757d'}}>{user.post}</span>
            </div>
        </div>
    }, [tag, handleChangeTag])

    return <div>
        <InputOverride value={searchValue} onChange={setSearchValue} placeholder='Поиск по ФИО'/>
        <div className='d-flex flex-column gap-2 mt-3' style={{overflow: 'auto', maxHeight: smallScreen ? '20vh' : '40vh'}}>
            {tag.users.map(user => renderOptionView(user))}
            {users
                .filter(user => tag.users.every(value => value.sAMAccountName !== user.sAMAccountName))
                .map(user => renderOptionView(user))}
        </div>
    </div>
}

function ProjectsTab({tag, handleChangeTag, strategies}: AddAdminTagTabProps) {
    const api = useAPI()
    const [searchValue, setSearchValue] = useState<string>('');
    const [selectedStrategy, setSelectedStrategy] = useState<object | null>(null)
    const projs = useTagTab<ProjectShort>(selectedStrategy
            ? `/projs?latest=true&short=true${getFetchProjectsLinkWithOptions(selectedStrategy)}&name=`
            : "/projs?latest=true&short=true&name=",
        searchValue)
    const fileInputRef = useRef(null)
    const smallScreen = useMediaQuery('(max-height: 730px)')

    const onLoadFile = async (event) => {
        const fileProjs = await api.postFormData('/projs/0/loadFromExcel', {}, event.target.files[0])
        if(!fileProjs.error){
            handleChangeTag(
                'projs',
                [...tag.projs, ...fileProjs.filter((proj: ProjectShort) => tag.projs.every(value => value.originId !== proj.originId))]
            )
        }
    }

    const renderOptionView = useCallback((proj: ProjectShort) => {
        return <div className='d-flex flex-column gap-1 pb-2' style={{borderBottom: '1px solid #d8d8d8'}} key={proj.id}>
            <div className='d-flex gap-2'>
                <input
                    type='checkbox'
                    defaultChecked={tag.projs.some(value => value.id === proj.id)}
                    onChange={(event) => handleChangeTag(
                        'projs',
                        event.target.checked ? [...tag.projs, proj] : tag.projs.filter(value => value.id !== proj.id)
                    )}
                />
                <span style={{
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    maxWidth: '85%',
                    overflow: 'hidden',
                    fontSize: 14
                }}>
                        {proj.fullNumber} {proj.name}
                    </span>
                <span className='ms-auto' style={{fontSize: 12}}>
                    {proj.fullNumber[0] === '6' ? 'Политика ЦТ' : `СП${proj.fullNumber[0]}`}
                </span>
            </div>
            {proj.head?.displayName && <div style={{fontSize: 12, color: '#6c757d'}}>
                Руководитель - {proj.head.displayName}
            </div>}
        </div>
    }, [tag, handleChangeTag])

    const strategiesOptions = useMemo(() => strategies.map(strategy => ({
        label: strategy.name,
        value: strategy
    })), [strategies])

    return <div>
        <div className='d-flex gap-3 mb-3 align-items-center'>
            <div style={{position: 'relative'}}>
                <Button variant='outline-primary' onClick={()=>fileInputRef.current?.click()}>
                    <PlusLg/> Загрузить Excel
                </Button>
                <div style={{position: 'absolute', top: -20, right: -20}}>
                    <TooltippedElement
                        tooltip='Обратите внимание, что файл должен быть формата XLSX или XLS и содержать для корректной загрузки либо номер, либо название проекта. Также обязательны заголовки столбцов: "Полный номер проекта" или "ИД версии", либо "Название проекта".'
                    />
                </div>
            </div>
            <span style={{fontWeight: 600}}>или</span>
            <input onChange={onLoadFile} multiple={false} ref={fileInputRef} type='file' hidden/>
        </div>
        <h3 className='mb-3' style={{fontWeight: 600}}>Выберите один или несколько проектов, чтобы привязать/отвязать их
            от данного тега</h3>
        <InputOverride value={searchValue} onChange={setSearchValue} placeholder='Поиск по названию'/>
        <Select
            className='mt-2'
            placeholder='Выберите из списка'
            options={strategiesOptions}
            onChange={opt => setSelectedStrategy(opt?.value)}
            value={selectedStrategy
                ? strategiesOptions.find(strategy => selectedStrategy.id === strategy.id)
                : null
            }
            isClearable
        />
        <div className='d-flex flex-column gap-2 mt-3' style={{overflow: 'auto', maxHeight: smallScreen ? '10vh' : '30vh'}}>
            {tag.projs.map(proj => renderOptionView(proj))}
            {projs
                .filter(proj => tag.projs.every(value => value.id !== proj.id))
                .map(proj => renderOptionView(proj))}
        </div>
    </div>
}

function InputOverride({value, onChange, placeholder}: InputOverrideProps) {
    return <Input
        startAdornment={<SearchIcon/>}
        endAdornment={<CloseButton onClick={() => onChange('')}/>}
        style={{
            width: '100%',
            paddingRight: 12,
            gap: 8
        }}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
    />
}

const getFetchProjectsLinkWithOptions = (option: any) => Object.entries(option.filterKeys).reduce((acc, [key, value]) => acc + `&${key}=${value}`, '')
